import { useEffect, useId, useRef, type ReactNode } from 'react';

interface ConfirmDialogProps {
  open: boolean;
  title: ReactNode;
  /** What the change will do, shown before anything is saved. */
  children: ReactNode;
  confirmLabel: string;
  busyLabel: string;
  destructive?: boolean;
  busy: boolean;
  error: string | null;
  onConfirm: () => void;
  onClose: () => void;
}

/** Confirm one change after showing its effect; the admin pages' write step. */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  busyLabel,
  destructive = false,
  busy,
  error,
  onConfirm,
  onClose,
}: ConfirmDialogProps) {
  const ref = useRef<HTMLDialogElement>(null);
  const titleId = useId();

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    else if (!open && dialog.open) dialog.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      onClose={onClose}
      // Escape must not dismiss a request in flight: the result needs a home.
      onCancel={event => {
        if (busy) event.preventDefault();
      }}
      aria-labelledby={titleId}
      className="pane m-auto w-[36rem] max-w-[calc(100vw-2rem)] p-0 text-ink shadow-lg backdrop:bg-black/35"
    >
      <div className="max-h-[70vh] overflow-y-auto px-4 pt-4 pb-3">
        <h2 id={titleId} className="text-sm font-semibold">
          {title}
        </h2>
        <div className="mt-2 text-xs leading-4 text-ink-2">{children}</div>
        {error && <p className="callout callout-err mt-3">{error}</p>}
      </div>
      <div className="flex flex-wrap items-center gap-2 border-t border-line px-4 py-3">
        <button
          type="button"
          disabled={busy}
          onClick={onConfirm}
          className={destructive ? 'btn btn-destructive' : 'btn btn-primary'}
        >
          {busy ? busyLabel : confirmLabel}
        </button>
        <button
          type="button"
          disabled={busy}
          onClick={onClose}
          className="btn btn-ghost"
        >
          Cancel
        </button>
      </div>
    </dialog>
  );
}
